import { createRef } from 'react';

import cs from 'classnames';
import { useRecoilValue } from 'recoil';

import { cn, ia, pm, spaceToKebabCase } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Loader from 'components/shared/Loader/Loader';
import state from 'components/state';

import { generateSubmenuKey, handleSubmenuPositioning } from '../Panel/lib/subPanelPosition';

import { getIsOptionActive } from './lib/getIsOptionActive';

const PopoverSeparator = ({ className }) => (
  <div className={cs('my-1 h-[1px] bg-neutral-200', className)} />
);

const PopoverOption = ({
  option,
  index,
  level,
  handleClose,
  parentPath = '',
  hoveredIndexes,
  handleMouseEnter,
  handleMouseLeave,
  handleOptionClick,
  activeOptionRef,
  submenuRefs,
  text,
  customButton,
  optionIconSize,
  optionIconColor,
  iconsShade,
  optionArrowIcon,
  optionArrowIconStroke,
  optionActiveIcon,
  optionArrowColor,
  position,
  renderOptions,
  separatorClassName,
  optionIconNoColor
}) => {
  const permissions = useRecoilValue(state.permissions);

  if (option.type === 'separator' || option.separator) {
    return (
      <PopoverSeparator
        key={`separator-${index}`}
        className={cs(option.className, separatorClassName)}
      />
    );
  }

  if (option?.permission) {
    const hasPermission = option?.permission?.isAny
      ? pm(permissions, option?.permission?.requiredPermissions, 'or')
      : pm(permissions, option?.permission?.requiredPermissions);

    if (!hasPermission) return null;
  }

  const label = option.label || option.title;
  const isActive = getIsOptionActive({ option, label, text, customButton });

  const currentPath = generateSubmenuKey(parentPath, level, index);
  const submenuKey = currentPath;

  if (!submenuRefs.current[submenuKey]) submenuRefs.current[submenuKey] = createRef();

  return (
    <div
      key={index}
      ref={isActive ? activeOptionRef : null}
      tabIndex={isActive ? 0 : -1}
      className={cn(
        'relative flex items-center justify-between gap-x-2',
        typeof option.component !== 'function' &&
          'cursor-pointer py-[6px] !pl-4 pr-2 hover:bg-neutral-50',
        option.className,
        option.onClick && !ia(option.children) ? 'cursor-pointer pr-4' : 'cursor-default',
        isActive && 'cursor-default bg-neutral-75 !pr-2 hover:bg-neutral-75',
        option.loading && 'cursor-not-allowed'
      )}
      data-qa={`popover-option-${spaceToKebabCase(label)}`}
      onMouseEnter={() => handleMouseEnter({ level, option, index })}
      onMouseLeave={(event) => handleMouseLeave({ level, option, event })}
      onClick={(event) =>
        !option.disabled && !option.loading && handleOptionClick(option, handleClose, event)
      }>
      {/* Disabled overlay */}
      {option.disabled && (
        <div className="absolute inset-0 z-[100] cursor-not-allowed bg-white opacity-40"></div>
      )}

      {/* Main content */}
      {(option.loading || option.icon || label) && (
        <div className={cs('flex grow items-center gap-x-2', option.className)}>
          {/* Icon or loader */}
          {option.loading ? (
            <Loader
              outlined
              className={`text-${optionIconColor}-500`}
              type={hoveredIndexes[level] === index ? 'secondary' : 'primary'}
            />
          ) : typeof option.icon === 'object' ? (
            option.icon
          ) : (
            option.icon && (
              <Icon
                icon={option.icon}
                size={optionIconSize}
                customColor={option.customColor}
                color={option.color || optionIconColor}
                disableColor={optionIconNoColor}
                shade={iconsShade}
                stroke={option.stroke}
              />
            )
          )}
          {/* Label */}
          {typeof option.label === 'object' ? (
            option.label
          ) : (
            <span
              className={cn(
                'grow select-none whitespace-nowrap text-sm font-400',
                option.textClassName,
                option.color === 'primary' || optionIconColor === 'primary'
                  ? 'text-primary-900'
                  : 'text-neutral-900'
              )}>
              {label}
            </span>
          )}

          {/* Right icon */}
          {typeof option.rightIcon === 'object'
            ? option.rightIcon
            : option.rightIcon && (
                <Icon
                  icon={option.rightIcon}
                  size={optionIconSize}
                  color={option.color || optionIconColor}
                  disableColor={optionIconNoColor}
                  shade={iconsShade}
                  stroke={option.stroke}
                />
              )}
        </div>
      )}

      {/* Component or arrow/active icon */}
      {typeof option.component === 'object' ? (
        option.component
      ) : typeof option.component === 'function' ? (
        option.component({ handleClose })
      ) : option.children ? (
        <Icon
          icon={optionArrowIcon}
          size={optionIconSize}
          className="ml-auto"
          color={optionArrowColor || optionIconColor}
          shade={iconsShade}
          stroke={optionArrowIconStroke}
        />
      ) : (
        optionActiveIcon &&
        isActive && (
          <Icon
            icon={optionActiveIcon}
            size={19}
            className="ml-auto"
            color={optionIconColor}
            shade={iconsShade}
          />
        )
      )}

      {/* Submenu */}
      {option.children && hoveredIndexes[level] === index && (
        <div
          ref={(node) => handleSubmenuPositioning(node, submenuKey, submenuRefs)}
          className={cs(
            'absolute z-50 mt-[6px] grid min-w-[120px] rounded-md bg-white py-2 shadow-[0px_2px_16px_0px_rgba(0,79,107,0.2)]',
            '-top-[14px]', // Default position
            option.position === 'left'
              ? 'right-full'
              : option.position === 'right'
                ? 'left-full'
                : position === 'left'
                  ? 'right-full'
                  : 'left-full'
          )}>
          {renderOptions(option.children, level + 1, handleClose, currentPath)}
        </div>
      )}
    </div>
  );
};

export default PopoverOption;
